import { router, useLocalSearchParams } from 'expo-router';
import { ChevronLeftIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { EventAttendees } from '@/components/events/event-attendees';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { fetchEventById, type Event } from '@/lib/events';

/**
 * Who is in and who is still missing, one tap from the scanner. `refresh`
 * carries the station's check-in count so returning here after scanning shows
 * the runners just added rather than a cached roster.
 */
export default function CheckInRosterScreen() {
  const { id, refresh } = useLocalSearchParams<{ id: string; refresh?: string }>();
  const insets = useSafeAreaInsets();
  const [event, setEvent] = React.useState<Event | null>(null);

  React.useEffect(() => {
    let active = true;

    fetchEventById(id)
      .then((loaded) => {
        if (active) {
          setEvent(loaded);
        }
      })
      .catch(() => {
        // The roster is the point of this screen; a missing title is survivable.
      });

    return () => {
      active = false;
    };
  }, [id]);

  return (
    <View className="flex-1 bg-background">
      <View className="flex-row items-center gap-2 px-4 py-3">
        <Pressable
          accessibilityLabel="Back to scanner"
          accessibilityRole="button"
          className="size-10 items-center justify-center rounded-full active:opacity-70"
          onPress={() => router.back()}
        >
          <Icon as={ChevronLeftIcon} className="size-6 text-foreground" />
        </Pressable>
        <View className="min-w-0 flex-1">
          <Text variant="label" className="text-muted-foreground">
            Attendance
          </Text>
          <Text className="text-lg font-bold" numberOfLines={1}>
            {event?.title ?? 'Event'}
          </Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 24 }}>
        <EventAttendees eventId={id} flat refreshKey={Number(refresh ?? 0)} />
      </ScrollView>
    </View>
  );
}
